import { InferSubjects } from '@casl/ability';
import { useAbility } from '@casl/react';
import { DialogProps } from '@reach/dialog';
import { AbilityContext, Can } from 'components/can';
import { ChangePlanDialog } from 'components/change-plan-dialog';
import { Button, Dialog, Text } from 'components/ui';
import { useDialogToggle } from 'hooks/use-dialog-toggle';
import Link from 'next/link';
import React from 'react';
import { useIntercom } from 'react-use-intercom';

interface Props extends DialogProps {
  onDismiss?: () => void;
}

export const UpgradeSubscriptionDialog = ({ isOpen, onDismiss }: Props) => {
  const [
    subscriptionDialogOpen,
    openSubscriptionDialog,
    closeSubscriptionDialog,
  ] = useDialogToggle(false);
  const { show } = useIntercom();
  const Message = () => {
    return (
      <>
        <Can I='update' a='Subscription'>
          <Text textAlign='center'>
            Your current subscription does not allow access to this feature,
            please upgrade or{' '}
            <Link href={'#'} onClick={show}>
              chat to us find out more.
            </Link>
          </Text>
        </Can>
        <Can not I='update' a='Subscription'>
          <Text>
            Your current subscription does not allow access to this feature.
            Please contact one of your organisation admins (see Company Profile
            page) to upgrade your subscription to access this feature or{' '}
            <Link href={'#'} onClick={show}>
              chat to us find out more.
            </Link>
          </Text>
        </Can>
      </>
    );
  };

  const actions = (
    <Can I='update' a='Subscription'>
      <Button
        size='medium'
        onClick={() => {
          openSubscriptionDialog();
          onDismiss?.();
        }}
      >
        Upgrade subscription
      </Button>
    </Can>
  );
  return (
    <>
      <Dialog
        title='Upgrade Subscription'
        isOpen={isOpen}
        onDismiss={onDismiss}
        actions={actions}
      >
        <Message />
      </Dialog>
      <ChangePlanDialog
        isOpen={subscriptionDialogOpen}
        onDismiss={closeSubscriptionDialog}
        upgrade={true}
      />
    </>
  );
};

export const SubscriptionGuard = ({
  children,
  featureName,
  trigger,
  action,
}: {
  children: (value: boolean, trigger: () => void) => React.ReactNode;
  featureName: InferSubjects<any>;
  trigger: () => void;
  action: 'create' | 'update' | 'read' | 'delete';
}) => {
  const ability = useAbility(AbilityContext);
  const [upgradeDialogOpen, openUpgradeDialog, closeUpgradeDialog] =
    useDialogToggle(false);
  const canRead = ability.can('read', featureName);
  const canUpdate = ability.can('update', featureName);
  const canCreate = ability.can('create', featureName);
  const canDelete = ability.can('delete', featureName);

  const getActionType = (action: 'create' | 'update' | 'read' | 'delete') => {
    switch (action) {
      case 'read':
        return canRead;
      case 'create':
        return canCreate;
      case 'update':
        return canUpdate;
      case 'delete':
        return canDelete;
      default:
        return canRead;
    }
  };

  return (
    <>
      {children(
        getActionType(action),
        getActionType(action) ? trigger : openUpgradeDialog
      )}
      <UpgradeSubscriptionDialog
        isOpen={upgradeDialogOpen}
        onDismiss={closeUpgradeDialog}
      />
    </>
  );
};
